import React from 'react';

import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import { fromCircle } from 'ol/geom/Polygon';
import { useDispatch, useSelector } from 'react-redux';

import ArrowBack from 'components/common/components/ArrowBack';
import PopupLoader from 'components/common/PopupLoader';
import { addAlert } from 'store/actions/alert/alerts';
import { authPostRequest } from 'helpers/apiRequests';
import { formStyles } from 'consts/styles/Form';

import OrganizationsFormRender from '../components/OrganizationsFormRender/OrganizationsFormRender';
import { validatePayload, validateCheck } from '../components/Helpers';

const AddOrganization = props => {
  const { parentOrganizationSelected, arrowBackClick } = props;
  const formClasses = formStyles();
  const dispatch = useDispatch();

  const useString = () => {
    const language = useSelector(state => state.language);
    return language;
  };

  const [alertsSubtitles] = React.useState(useString().alerts);
  const [seeableLoader, setSeeableLoader] = React.useState(false);
  const [selectedOrgTitle] = React.useState(
    useString().adminZone.addOrganization.selectedOrgTitle_
  );
  const [componentHeader] = React.useState(
    useString().adminZone.addOrganization.header_
  );
  const [values, setValues] = React.useState({
    name: '',
    phone: '',
    city: '',
    street: '',
    number: '',
    geojson: null,
    parent: '',
    master: '',
    email: '',
    buffer: 0,
    nip: '',
    regon: '',
  });

  const [errors, setErrors] = React.useState({
    map: false,
    name: false,
    phone: false,
    email: false,
    city: false,
    street: false,
    number: false,
    nip: false,
    regon: false,
    buffer: false,
  });

  const [displayErrors, setDisplayErrors] = React.useState(false);

  const onParentOrgSelect = () => {
    if (parentOrganizationSelected.noOrganizationsToSelect) {
      parentOrganizationSelected.master = null;
      parentOrganizationSelected.parent = null;
    }

    setValues({
      ...values,
      master: parentOrganizationSelected.master
        ? parentOrganizationSelected.master
        : parentOrganizationSelected.uid,
      parent: parentOrganizationSelected.uid,
    });
  };

  React.useEffect(onParentOrgSelect, []);

  const handleChange = event => {
    const { target } = event;
    const { name, value } = target;
    const ifCheckIsTrue = validateCheck(name, value);

    setValues({
      ...values,
      [name]: value,
    });

    setErrors({ ...errors, [name]: !ifCheckIsTrue });
  };

  const onGeometryChange = value => {
    setValues({
      ...values,
      geojson: value,
    });
  };

  const isDataValid = () => {
    let isValid = true;
    Object.keys(values).forEach(key => {
      if (values[key] === '') {
        setErrors({ ...errors, [key]: true });
        isValid = false;
      }
    });
    const err = validatePayload(values);
    if (err.length > 0) {
      const newErrors = Object.keys(errors).forEach(
        key => (errors[key] = !!err.includes(key))
      );
      setDisplayErrors(true);
      isValid = false;
    }
    return isValid;
  };

  const add = async () => {
    if (!isDataValid()) return;
    setSeeableLoader(true);
    const data = { ...values };
    data.address = {
      city: values.city,
      street: values.street,
      number: values.number,
    };
    delete data.city;
    delete data.street;
    delete data.number;

    // if circle was drawn its geometry is sending to service to map it to polygon
    if (values?.geojson?.type === 'GeometryCollection') {
      const polygon = fromCircle(values.geojson.getGeometry(), 16);

      data.geojson = {
        type: 'Polygon',
        coordinates: polygon
          .transform('EPSG:3857', 'EPSG:4326')
          .getCoordinates(),
      };
    } else data.geojson = values.geojson;

    if (data.geojson === null) {
      dispatch(
        addAlert({
          title: alertsSubtitles[2].title_,
          desc: alertsSubtitles[2].desc_,
          type: 'error',
        })
      );
      setErrors({ ...errors, map: true });
    }
    const result = await authPostRequest('organization', data);
    if (result.status === 201) {
      dispatch(
        addAlert({
          title: alertsSubtitles.addDataSuccess.title_,
          desc: alertsSubtitles.addDataSuccess.desc_,
          type: 'success',
        })
      );
      arrowBackClick();
      setSeeableLoader(false);
    } else
      dispatch(
        addAlert({
          title: alertsSubtitles[result.data.code].title_,
          desc: alertsSubtitles[result.data.code].desc_,
          type: 'error',
        })
      );
    setSeeableLoader(false);
  };

  return (
    <>
      {seeableLoader && <PopupLoader />}
      <div
        className={`${formClasses.Form}--flex ${formClasses.Form}--flex--center ${formClasses.Form}__text-field`}
      >
        <ArrowBack clickFunction={arrowBackClick} fromLeft={0} />
        <Typography
          variant="h3"
          className={`${formClasses.Form}__text-field__title`}
        >
          {componentHeader}
        </Typography>
      </div>
      <div
        className={`${formClasses.Form}--flex ${formClasses.Form}--flex--center ${formClasses.Form}__text-field`}
      >
        <Typography
          variant="h4"
          className={`${formClasses.Form}__text-field__title`}
        >
          {selectedOrgTitle}: <b>{parentOrganizationSelected.name}</b>
        </Typography>
      </div>

      <OrganizationsFormRender
        add={add}
        handleChange={handleChange}
        onGeometryChange={onGeometryChange}
        drawnGeometry={values.geojson}
        values={values}
        displayErrors={displayErrors}
        buttonText={useString().adminZone.addOrganization.formFields.addButton_}
        strings={useString().adminZone.addOrganization}
        newOrganization
        errors={errors}
        setErrors={setErrors}
      />
    </>
  );
};

AddOrganization.propTypes = {
  parentOrganizationSelected: PropTypes.object.isRequired,
  arrowBackClick: PropTypes.func.isRequired,
};

export default AddOrganization;
